import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { ConnectedUser } from '../../stores/types/connection';
import { GreenroomState as GS } from '../../stores/types/types';
import type { ShotFeed } from '../../stores/types/shots';
import { FEED_TYPES } from '../../stores/reducers/shots';
import { contrastingColor, getNameOrInitials } from '../../utils/utils';
import ThumbnailOverlay from './thumbnail-badge';
import useShots from '../../contexts/shots/provider';
import type { DragItem } from '../../stores/types/user-interface';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import useDragItem from '../../hooks/use-dnd/use-drag-item';
import { setOpenContextMenuId } from '../../stores/actions/user-interface';
import VideoFeedContextMenu from './video-feed-context-menu';
import RestrictedUserTooltip from './restricted-user-tooltip';
import useProfile from '../../hooks/use-profile';
import { SafeProfileInterface } from '../../stores/types/user-profiles';
import colors from '../../utils/colors';

interface Props {
	restrictedUser: ConnectedUser;
	feedsRef?: React.MutableRefObject<HTMLDivElement | null>;
	isForShotModal?: boolean;
}

export default function RestrictedUserThumbnail(props: Props) {
	const dispatch = useDispatch();

	const [rightClickCoords, setRightClickCoords] = useState<[number, number]>();

	const wrapperRef = useRef<HTMLDivElement | null>(null);

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const onsiteUsers = useSelector((s: GS) => s.SessionReducer.userMeetingSettings.onsiteUsers);
	const useMasks = useSelector((s: GS) => s.SettingsReducer.featureFlags.use_profile_masks);

	const {
		restrictedUser,
	} = props;

	const {watchedProfile: mProfile} = useProfile({suggestedProfile: restrictedUser.gr_user});
	const watchedProfile = (useMasks && mProfile) ? mProfile : {
		...restrictedUser,
		bg_image: restrictedUser.poster
	}; 

	const {
		currentShot: {
			shot_data: {
				shotFeeds,
			},
		},
	} = useShots();

	// The right click context menu requires a unique string id
	const contextMenuId = useMemo(() => `video-feed-${restrictedUser.gr_user}`, [restrictedUser]);

	const isOnsiteUser = useMemo(
		() => !!onsiteUsers?.includes(restrictedUser.gr_user),
		[onsiteUsers, restrictedUser],
	);

	const inShot = useMemo(() => shotFeeds.some((feed: ShotFeed) => {
		if (feed.feedType === FEED_TYPES.EMAIL) {
			return feed.email === restrictedUser.email;
		} if (feed.feedType === FEED_TYPES.PRESENTER) {
			return feed.presenter === restrictedUser.gr_user;
		}

		return null;
	}), [shotFeeds]);

	const dndProps = useMemo<DragItem>(() => {
		if (restrictedUser.gr_user) {
			return {
				id: restrictedUser.gr_user,
				type: DRAG_TYPE.FEED_PRESENTER,
			};
		}
		return {
			id: restrictedUser.email,
			type: DRAG_TYPE.FEED_EMAIL,
		};
	}, [restrictedUser]);

	const colorStyles = useMemo(() => {
		const colorObj = {
			backgroundColor: '#333',
			color: contrastingColor(colors.gray33),
		};

		if (meeting?.primary_color) {
			colorObj.backgroundColor = `#${meeting.primary_color}`;
			colorObj.color = contrastingColor(meeting.primary_color);
		}

		return colorObj;
	}, [meeting]);

	const { dragRef, isDragging } = useDragItem(dndProps, host);

	const labelTextOption1 = isOnsiteUser
		? 'ONSITE'
		: '';
	const labelTextOption2 = restrictedUser.isProducer
		? 'PRODUCER'
		: labelTextOption1;
	const labelText = isOnsiteUser && restrictedUser.isProducer
		? 'PROD-ONSITE'
		: labelTextOption2;

	return (
		<>
			{!!restrictedUser.gr_user && (
				<VideoFeedContextMenu
					clickCoordinates={rightClickCoords}
					contextMenuId={contextMenuId}
					socket={null}
					userId={restrictedUser.gr_user}
				/>
			)}
			<React.Fragment>
				{isDragging && (<div className="video-thumb-drag-placeholder" />)}
				<div
					onContextMenu={(e) => {
						if (host) {
							e.preventDefault();
							setRightClickCoords([e.pageX, e.pageY]);
							dispatch(setOpenContextMenuId(contextMenuId));
						}
					}}
					onFocus={(e) => e.currentTarget.blur()}
					id={restrictedUser.socketId}
					className={classNames('video-thumb', { 'is-dragging': isDragging })}
					ref={dragRef}
				>
					<div className="video-wrapper" ref={wrapperRef}>
						<div className={classNames('video-overlay')}>
							<div className={classNames('poster')}>
								{(watchedProfile.bg_image || watchedProfile.avatar) ? (
									<img className="poster-image" src={watchedProfile.bg_image || watchedProfile.avatar} alt={''} />
								) : (
									<div className="no-image-placeholder">
										<span style={colorStyles}>
											{getNameOrInitials(watchedProfile, false)}
										</span>
									</div>
								)}
							</div>
							<RestrictedUserTooltip
								restrictedUserState={restrictedUser.restrictedUserState}
							/>
							<ThumbnailOverlay
								userObjForFeed={restrictedUser}
								videoIsOnCanvas={inShot}
								isScreenShare={false}
								videoWebRtcId={null}
							/>
							<div className="video-overlay-bottom">
								<label className="font-size-14 font-weight-heavy">{getNameOrInitials(watchedProfile, true)}</label>
							</div>
							{!!labelText && (
								<div className="producer-label" data-testid="video-feed-restricted-thumbnail-label">
									<span>{labelText}</span>
								</div>
							)}
						</div>

						<div className={classNames('selection-border', inShot && 'in-shot')}></div>
					</div>
				</div>
			</React.Fragment>
		</>
	);
}
